import classNames from "classnames";
import React, { FC, useRef, useState } from "react";
import { LuMic, LuSparkles, LuSquare, LuStar, LuUsers } from "react-icons/lu";
import { Link, useNavigate } from "react-router-dom";

import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { useAiReportSpecMutation } from "../../../slices/AiSlice";
import { useGetCustomReportsQuery } from "../../../slices/CustomReportsSlice";
import { aiErrorMessage } from "../../ai/ai.utils";
import {
  appendDictation,
  useDictation,
} from "../../work/pages/details/widgets/work-notes/useDictation";
import { formatDate } from "../reports.utils";
import { useReportsAccess } from "../reportsAccess";
import { STANDARD_REPORTS } from "../saved/savedView";
import { CustomReportDraft } from "./CustomReportPage";
import { needsBilling, normalizeSpec } from "./reportSpec";

const EXAMPLES = [
  "Hours per team member per month this year, billable vs non-billable",
  "Unpaid invoice balance by client, largest first",
  "Work completed per month this year compared to last year",
];

/** Reports home: "Create a report with AI" (owner 2026-10-25). Shown only when AI is on. */
export const CreateReportBox: FC = () => {
  const navigate = useNavigate();
  const access = useReportsAccess();
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [makeSpec, call] = useAiReportSpecMutation();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Microphone (owner 2026-11-05), like the top search box and the Ask panel.
  const dictation = useDictation((spoken) => {
    setText((current) => appendDictation(current, spoken).slice(0, 1000));
    inputRef.current?.focus();
  });

  const create = async (request: string) => {
    const r = request.trim();
    if (!r || call.isLoading) return;
    setError("");
    try {
      const res = await makeSpec({
        orgId: getLinkedFirm()?.orgId || "",
        request: r,
      }).unwrap();
      const spec = normalizeSpec(res.spec);
      if (!spec) throw new Error("bad spec");
      if (needsBilling(spec) && !access.billing) {
        setError(
          "That report needs billing access (Admins and people with View Billing permission).",
        );
        return;
      }
      const draft: CustomReportDraft = { spec, request: r };
      navigate("/reports/custom/new", { state: { draft } });
    } catch (e) {
      setError(
        e instanceof Error && e.message === "bad spec"
          ? "I couldn't build that report. Try describing it differently."
          : aiErrorMessage(e),
      );
    }
  };

  return (
    <section
      aria-labelledby={"reports-create-ai"}
      className={
        "rounded-[16px] border border-[#D9D1FF] bg-white px-5 py-[18px] shadow-[0_6px_18px_rgba(107,78,255,0.08)]"
      }>
      <div className={"flex items-center gap-2"}>
        <span
          className={
            "flex h-[26px] w-[26px] items-center justify-center rounded-lg bg-[#F1EEFF] text-[#6B4EFF]"
          }>
          <LuSparkles size={14} />
        </span>
        <h2 id={"reports-create-ai"} className={"text-base font-extrabold"}>
          Create a report with AI
        </h2>
      </div>
      <p className={"mb-3 mt-1 text-[13px] text-[#5B6472]"}>
        Describe the report in your own words. ProCharted builds a full report
        page you can save, filter, share and export.
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create(text);
        }}>
        <div className={"relative"}>
          <textarea
            ref={inputRef}
            aria-label={"Describe the report"}
            rows={2}
            maxLength={1000}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                create(text);
              }
            }}
            placeholder={
              "e.g. Hours per work type this year compared to last year"
            }
            className={classNames(
              "w-full resize-none rounded-[12px] border border-[#DCE2EA] px-3.5 py-3 text-[14px] leading-[1.5] outline-none focus:border-[#6B4EFF]",
              dictation.supported && "pr-14",
            )}
          />
          {dictation.supported && (
            <button
              type={"button"}
              aria-label={
                dictation.isListening
                  ? "Stop dictation"
                  : "Describe it by voice"
              }
              title={
                dictation.error ||
                (dictation.isListening
                  ? "Listening… press again to stop"
                  : "Speak instead of typing")
              }
              onMouseDown={(event) => event.preventDefault()}
              onClick={dictation.isListening ? dictation.stop : dictation.start}
              className={classNames(
                "absolute right-2 top-2 flex h-10 w-10 items-center justify-center rounded-[10px]",
                dictation.isListening
                  ? "animate-pulse bg-[#FDECEC] text-[#E5484D]"
                  : dictation.error
                  ? "text-[#E5484D] hover:bg-[#FDECEC]"
                  : "text-[#7A8194] hover:bg-[#F4F2FE] hover:text-[#6B4EFF]",
              )}>
              {dictation.isListening ? (
                <LuSquare size={12} fill={"currentColor"} />
              ) : (
                <LuMic size={18} strokeWidth={2.2} />
              )}
            </button>
          )}
        </div>
        <div className={"mt-2.5 flex flex-wrap items-center gap-2"}>
          {EXAMPLES.map((example) => (
            <button
              key={example}
              type={"button"}
              onClick={() => setText(example)}
              className={
                "rounded-full border border-[#D9D1FF] bg-[#F5F3FF] px-3 py-1 text-[12px] font-semibold text-[#6B4EFF] hover:bg-[#ECE7FF]"
              }>
              {example}
            </button>
          ))}
          <button
            type={"submit"}
            disabled={call.isLoading || !text.trim()}
            className={
              "ml-auto inline-flex h-10 items-center gap-2 rounded-[10px] bg-[#6B4EFF] px-4 text-[13px] font-bold text-white hover:bg-[#5A3EE6] disabled:opacity-50"
            }>
            <LuSparkles size={15} />
            {call.isLoading ? "Building your report…" : "Create report"}
          </button>
        </div>
      </form>
      {error && (
        <div
          role={"alert"}
          className={
            "mt-2.5 rounded-[10px] border border-[#FAD4D0] bg-[#FEF3F2] px-3 py-2 text-[13px] font-semibold text-[#B42318]"
          }>
          {error}
        </div>
      )}
    </section>
  );
};

/** Reports home: the person's own AI-created reports + the ones shared with the firm. */
export const MyReports: FC = () => {
  const orgId = getLinkedFirm()?.orgId || "";
  const { data } = useGetCustomReportsQuery({ orgId }, { skip: !orgId });
  const reports = data?.reports ?? [];
  if (!data?.available || !reports.length) return null;
  return (
    <section aria-labelledby={"reports-mine"} className={"flex flex-col gap-3"}>
      <div className={"flex flex-wrap items-baseline gap-2.5"}>
        <h2 id={"reports-mine"} className={"text-lg font-extrabold"}>
          My reports
        </h2>
        <span
          className={
            "rounded-full bg-[#F2F4F7] px-2 py-[1px] text-xs font-bold text-[#5B6472]"
          }>
          {reports.length}
        </span>
        <span className={"text-[13px] text-[#5B6472]"}>
          Saved by you, or shared by your team
        </span>
      </div>
      <div
        className={"grid gap-3.5"}
        style={{
          gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))",
        }}>
        {reports.map((r) => {
          const standard = r.kind === "standard";
          return (
            <Link
              key={r.id}
              to={
                standard ? `/reports/saved/${r.id}` : `/reports/custom/${r.id}`
              }
              className={
                "flex min-h-[150px] flex-col gap-2 rounded-[14px] border border-[#EDEFF3] bg-white px-5 py-[18px] text-[#1F2433] no-underline hover:border-[#6B4EFF] hover:shadow-[0_6px_18px_rgba(31,36,51,0.08)]"
              }>
              <span className={"flex flex-wrap justify-between gap-2"}>
                {standard ? (
                  <span
                    className={
                      "inline-flex items-center gap-1 rounded-full bg-[#F2F4F7] px-2 py-[3px] text-[11px] font-bold text-[#3F4757]"
                    }>
                    <LuStar size={11} /> Based on:{" "}
                    {STANDARD_REPORTS[r.basedOn || ""]?.name || r.description}
                  </span>
                ) : (
                  <span
                    className={
                      "inline-flex items-center gap-1 rounded-full bg-[#F1EEFF] px-2 py-[3px] text-[11px] font-bold text-[#6B4EFF]"
                    }>
                    <LuSparkles size={11} /> Created with AI
                  </span>
                )}
                {r.shared && (
                  <span
                    className={classNames(
                      "inline-flex items-center gap-1 rounded-full bg-[#ECFDF3] px-2 py-[3px] text-[11px] font-bold text-[#12805C]",
                    )}>
                    <LuUsers size={11} /> Shared with team
                  </span>
                )}
              </span>
              <span className={"text-[15.5px] font-extrabold"}>{r.name}</span>
              {standard
                ? !!r.labels?.length && (
                    <span className={"flex flex-wrap gap-1.5"}>
                      {r.labels.map((l) => (
                        <span
                          key={l}
                          className={
                            "rounded-full border border-[#E4E7EC] bg-[#F7F8FA] px-2 py-[2px] text-[11.5px] font-semibold text-[#3A4256]"
                          }>
                          {l}
                        </span>
                      ))}
                    </span>
                  )
                : r.description && (
                    <span
                      className={"text-[13px] leading-[1.45] text-[#5B6472]"}>
                      {r.description}
                    </span>
                  )}
              <span
                className={"mt-auto text-[12px] font-semibold text-[#9097A8]"}>
                {r.mine ? "You" : r.createdByName || "A team member"} ·{" "}
                {formatDate(r.updatedAt)}
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
};
